import {
  BLOCKS_PER_SECTION, CHUNK_X, CHUNK_Z, DIMENSIONS, NIBBLES_PER_SECTION,
  SECTION_COUNT, WORLD_MAX_Y, WORLD_MIN_Y, type DimensionId,
} from '../shared/constants.ts';
import {
  ByteReader, ByteWriter, C2S, PROTOCOL_VERSION, S2C, readSection, readStack,
  type NetItemStack,
} from '../shared/net/protocol.ts';
import { Chunk, Section } from '../shared/world/chunk.ts';

/**
 * Client-side networking.
 *
 * Decodes the binary protocol into plain events. Chunk payloads are turned
 * straight into `Chunk` objects so the client and server share one storage
 * implementation.
 */

export interface HelloInfo {
  playerId: number;
  seed: number;
  dimension: DimensionId;
  x: number;
  y: number;
  z: number;
  yaw: number;
  pitch: number;
  time: number;
  name: string;
  viewDistance: number;
}

export interface EntityInfo {
  id: number;
  kind: number;
  type: string;
  x: number;
  y: number;
  z: number;
  yaw: number;
  pitch: number;
  health: number;
  maxHealth: number;
  stack: NetItemStack | null;
  blockState: number;
  xp: number;
}

export interface PlayerStateInfo {
  health: number;
  food: number;
  saturation: number;
  xpLevel: number;
  xpProgress: number;
  air: number;
  dead: boolean;
  gameMode: number;
}

export interface ContainerInfo {
  id: number;
  kind: string;
  slotCount: number;
  x: number;
  y: number;
  z: number;
}

export interface NetHandlers {
  onHello(info: HelloInfo): void;
  onChunk(chunk: Chunk): void;
  onChunkUnload(cx: number, cz: number): void;
  onBlockChange(x: number, y: number, z: number, state: number): void;
  onMultiBlockChange(cx: number, cz: number, changes: { x: number; y: number; z: number; state: number }[]): void;
  onLight(cx: number, cz: number, sections: { sy: number; sky: Uint8Array; block: Uint8Array }[]): void;
  onTime(time: number, tick: number): void;
  onPlayerState(state: PlayerStateInfo): void;
  onPositionCorrection(x: number, y: number, z: number, yaw: number, pitch: number, sequence: number): void;
  onInventory(slots: (NetItemStack | null)[], selected: number, carried: NetItemStack | null): void;
  onEntitySpawn(info: EntityInfo): void;
  onEntityUpdate(id: number, x: number, y: number, z: number, yaw: number, pitch: number, health: number, hurt: boolean): void;
  onEntityRemove(id: number): void;
  onChat(message: string): void;
  onOpenContainer(info: ContainerInfo): void;
  onContainerUpdate(id: number, slots: (NetItemStack | null)[], carried: NetItemStack | null, furnace: { burn: number; cook: number } | null): void;
  onCloseContainer(): void;
  onSound(name: string, x: number, y: number, z: number, volume: number, pitch: number): void;
  onParticle(kind: string, x: number, y: number, z: number, amount: number): void;
  onDimension(dimension: DimensionId, x: number, y: number, z: number): void;
  onBreakProgress(playerId: number, x: number, y: number, z: number, stage: number): void;
  onRespawnInfo(x: number, y: number, z: number): void;
  onBossBar(boss: { name: string; progress: number; crystals: number } | null): void;
  onDisconnect(reason: string): void;
}

export class NetClient {
  private socket: WebSocket | null = null;
  private readonly handlers: NetHandlers;
  private sequence = 0;
  connected = false;
  /** Rolling round-trip estimate, milliseconds. */
  latency = 0;
  private lastPingSent = 0;

  constructor(handlers: NetHandlers) {
    this.handlers = handlers;
  }

  connect(url: string, name: string, uuid: string): void {
    const socket = new WebSocket(url);
    socket.binaryType = 'arraybuffer';
    this.socket = socket;

    socket.onopen = () => {
      this.connected = true;
      const w = new ByteWriter(64);
      w.u8(C2S.JOIN);
      w.varint(PROTOCOL_VERSION);
      w.str(name);
      w.str(uuid);
      socket.send(w.finish());
    };
    socket.onmessage = (ev) => {
      try {
        this.handle(new Uint8Array(ev.data as ArrayBuffer));
      } catch (err) {
        console.error('packet decode error', err);
      }
    };
    socket.onclose = (ev) => {
      this.connected = false;
      this.handlers.onDisconnect(ev.reason || 'connection closed');
    };
    socket.onerror = () => {
      this.handlers.onDisconnect('connection error');
    };
  }

  private send(data: Uint8Array): void {
    if (this.socket?.readyState === WebSocket.OPEN) this.socket.send(data);
  }

  get bufferedAmount(): number {
    return this.socket?.bufferedAmount ?? 0;
  }

  // -------------------------------------------------------------------------
  // Outbound
  // -------------------------------------------------------------------------

  sendMove(x: number, y: number, z: number, yaw: number, pitch: number, onGround: boolean, sneak: boolean, sprint: boolean): number {
    const w = new ByteWriter(48);
    w.u8(C2S.MOVE);
    w.varint(++this.sequence);
    w.f64(x);
    w.f64(y);
    w.f64(z);
    w.f32(yaw);
    w.f32(pitch);
    w.u8((onGround ? 1 : 0) | (sneak ? 2 : 0) | (sprint ? 4 : 0));
    this.send(w.finish());
    return this.sequence;
  }

  sendBreakStart(x: number, y: number, z: number): void {
    const w = new ByteWriter(16);
    w.u8(C2S.BREAK_START);
    w.i32(x);
    w.i16(y);
    w.i32(z);
    this.send(w.finish());
  }

  sendBreakCancel(): void {
    const w = new ByteWriter(4);
    w.u8(C2S.BREAK_CANCEL);
    this.send(w.finish());
  }

  sendBreakFinish(x: number, y: number, z: number): void {
    const w = new ByteWriter(16);
    w.u8(C2S.BREAK_FINISH);
    w.i32(x);
    w.i16(y);
    w.i32(z);
    this.send(w.finish());
  }

  sendPlace(x: number, y: number, z: number, face: number, cx: number, cy: number, cz: number): void {
    const w = new ByteWriter(32);
    w.u8(C2S.PLACE);
    w.i32(x);
    w.i16(y);
    w.i32(z);
    w.u8(face);
    w.f32(cx);
    w.f32(cy);
    w.f32(cz);
    this.send(w.finish());
  }

  sendSelectSlot(slot: number): void {
    const w = new ByteWriter(4);
    w.u8(C2S.SELECT_SLOT);
    w.u8(slot);
    this.send(w.finish());
  }

  sendInventoryClick(windowId: number, slot: number, button: number, mode: number): void {
    const w = new ByteWriter(16);
    w.u8(C2S.INVENTORY_CLICK);
    w.varint(windowId);
    w.svarint(slot);
    w.u8(button);
    w.u8(mode);
    this.send(w.finish());
  }

  sendDrop(wholeStack: boolean): void {
    const w = new ByteWriter(4);
    w.u8(C2S.DROP);
    w.u8(wholeStack ? 1 : 0);
    this.send(w.finish());
  }

  sendChat(message: string): void {
    const w = new ByteWriter(message.length + 8);
    w.u8(C2S.CHAT);
    w.str(message);
    this.send(w.finish());
  }

  sendRespawn(): void {
    const w = new ByteWriter(4);
    w.u8(C2S.RESPAWN);
    this.send(w.finish());
  }

  sendAttack(entityId: number): void {
    const w = new ByteWriter(8);
    w.u8(C2S.ATTACK);
    w.varint(entityId);
    this.send(w.finish());
  }

  sendInteract(entityId: number): void {
    const w = new ByteWriter(8);
    w.u8(C2S.INTERACT);
    w.varint(entityId);
    this.send(w.finish());
  }

  sendCloseContainer(): void {
    const w = new ByteWriter(4);
    w.u8(C2S.CLOSE_CONTAINER);
    this.send(w.finish());
  }

  sendUseItem(): void {
    const w = new ByteWriter(4);
    w.u8(C2S.USE_ITEM);
    this.send(w.finish());
  }

  sendRenderDistance(distance: number): void {
    const w = new ByteWriter(4);
    w.u8(C2S.SET_RENDER_DISTANCE);
    w.u8(distance);
    this.send(w.finish());
  }

  sendToggleFly(): void {
    const w = new ByteWriter(4);
    w.u8(C2S.TOGGLE_FLY);
    this.send(w.finish());
  }

  // -------------------------------------------------------------------------
  // Inbound
  // -------------------------------------------------------------------------

  private handle(data: Uint8Array): void {
    const r = new ByteReader(data);
    const type = r.u8();
    switch (type) {
      case S2C.HELLO: {
        const version = r.varint();
        if (version !== PROTOCOL_VERSION) {
          this.handlers.onDisconnect(`protocol mismatch (server ${version}, client ${PROTOCOL_VERSION})`);
          return;
        }
        this.handlers.onHello({
          playerId: r.varint(),
          seed: r.i32(),
          dimension: DIMENSIONS[r.u8()] ?? 'overworld',
          x: r.f64(),
          y: r.f64(),
          z: r.f64(),
          yaw: r.f32(),
          pitch: r.f32(),
          time: r.u32(),
          name: r.str(),
          viewDistance: r.u8(),
        });
        return;
      }
      case S2C.CHUNK: return this.readChunk(r);
      case S2C.CHUNK_UNLOAD: {
        this.handlers.onChunkUnload(r.svarint(), r.svarint());
        return;
      }
      case S2C.BLOCK_CHANGE: {
        this.handlers.onBlockChange(r.i32(), r.i16(), r.i32(), r.varint());
        return;
      }
      case S2C.MULTI_BLOCK_CHANGE: {
        const cx = r.svarint();
        const cz = r.svarint();
        const count = r.varint();
        const changes: { x: number; y: number; z: number; state: number }[] = [];
        for (let i = 0; i < count; i++) {
          const lx = r.u8();
          const y = r.i16();
          const lz = r.u8();
          const state = r.varint();
          changes.push({ x: cx * CHUNK_X + lx, y, z: cz * CHUNK_Z + lz, state });
        }
        this.handlers.onMultiBlockChange(cx, cz, changes);
        return;
      }
      case S2C.LIGHT: {
        const cx = r.svarint();
        const cz = r.svarint();
        const mask = r.u32();
        const sections: { sy: number; sky: Uint8Array; block: Uint8Array }[] = [];
        for (let sy = 0; sy < SECTION_COUNT; sy++) {
          if ((mask & (1 << sy)) === 0) continue;
          sections.push({
            sy,
            sky: new Uint8Array(r.bytes(NIBBLES_PER_SECTION)),
            block: new Uint8Array(r.bytes(NIBBLES_PER_SECTION)),
          });
        }
        this.handlers.onLight(cx, cz, sections);
        return;
      }
      case S2C.TIME: {
        this.handlers.onTime(r.u32(), r.u32());
        return;
      }
      case S2C.PLAYER_STATE: {
        this.handlers.onPlayerState({
          health: r.f32(),
          food: r.u8(),
          saturation: r.f32(),
          xpLevel: r.varint(),
          xpProgress: r.f32(),
          air: r.u8(),
          dead: r.u8() === 1,
          gameMode: r.u8(),
        });
        return;
      }
      case S2C.POSITION_CORRECTION: {
        this.handlers.onPositionCorrection(r.f64(), r.f64(), r.f64(), r.f32(), r.f32(), r.varint());
        return;
      }
      case S2C.INVENTORY: {
        const count = r.varint();
        const slots: (NetItemStack | null)[] = [];
        for (let i = 0; i < count; i++) slots.push(readStack(r));
        const selected = r.u8();
        const carried = readStack(r);
        this.handlers.onInventory(slots, selected, carried);
        return;
      }
      case S2C.ENTITY_SPAWN: {
        const id = r.varint();
        const kind = r.u8();
        const info: EntityInfo = {
          id,
          kind,
          type: r.str(),
          x: r.f32(),
          y: r.f32(),
          z: r.f32(),
          yaw: r.f32(),
          pitch: r.f32(),
          health: r.f32(),
          stack: readStack(r),
          blockState: r.remaining > 0 ? r.varint() : 0,
          xp: r.remaining > 0 ? r.varint() : 0,
          maxHealth: r.remaining > 0 ? r.f32() : 20,
        };
        this.handlers.onEntitySpawn(info);
        return;
      }
      case S2C.ENTITY_UPDATE: {
        const id = r.varint();
        this.handlers.onEntityUpdate(id, r.f32(), r.f32(), r.f32(), r.f32(), r.f32(), r.f32(), r.remaining > 0 ? r.u8() === 1 : false);
        return;
      }
      case S2C.ENTITY_REMOVE: {
        this.handlers.onEntityRemove(r.varint());
        return;
      }
      case S2C.CHAT: {
        this.handlers.onChat(r.str());
        return;
      }
      case S2C.OPEN_CONTAINER: {
        this.handlers.onOpenContainer({
          id: r.varint(),
          kind: r.str(),
          slotCount: r.varint(),
          x: r.i32(),
          y: r.i16(),
          z: r.i32(),
        });
        return;
      }
      case S2C.CONTAINER_UPDATE: {
        const id = r.varint();
        const count = r.varint();
        const slots: (NetItemStack | null)[] = [];
        for (let i = 0; i < count; i++) slots.push(readStack(r));
        const carried = readStack(r);
        const hasFurnace = r.remaining > 0 && r.u8() === 1;
        const furnace = hasFurnace ? { burn: r.varint(), cook: r.varint() } : null;
        this.handlers.onContainerUpdate(id, slots, carried, furnace);
        return;
      }
      case S2C.CLOSE_CONTAINER: {
        this.handlers.onCloseContainer();
        return;
      }
      case S2C.SOUND: {
        this.handlers.onSound(r.str(), r.f32(), r.f32(), r.f32(), r.f32(), r.f32());
        return;
      }
      case S2C.PARTICLE: {
        this.handlers.onParticle(r.str(), r.f32(), r.f32(), r.f32(), r.f32());
        return;
      }
      case S2C.DIMENSION: {
        this.handlers.onDimension(DIMENSIONS[r.u8()] ?? 'overworld', r.f32(), r.f32(), r.f32());
        return;
      }
      case S2C.BLOCK_BREAK_PROGRESS: {
        this.handlers.onBreakProgress(r.varint(), r.i32(), r.i32(), r.i32(), r.u8());
        return;
      }
      case S2C.BOSS_BAR: {
        if (r.u8() === 0) {
          this.handlers.onBossBar(null);
        } else {
          this.handlers.onBossBar({ name: r.str(), progress: r.f32(), crystals: r.u8() });
        }
        return;
      }
      case S2C.RESPAWN_INFO: {
        this.handlers.onRespawnInfo(r.f32(), r.f32(), r.f32());
        return;
      }
      default:
        return;
    }
  }

  private readChunk(r: ByteReader): void {
    const cx = r.svarint();
    const cz = r.svarint();
    const chunk = new Chunk(cx, cz);
    chunk.biomes.set(r.bytes(CHUNK_X * CHUNK_Z));
    const mask = r.u32();

    for (let sy = 0; sy < SECTION_COUNT; sy++) {
      if ((mask & (1 << sy)) === 0) continue;
      const blocks = new Uint16Array(BLOCKS_PER_SECTION);
      readSection(r, blocks);
      const sky = new Uint8Array(r.bytes(NIBBLES_PER_SECTION));
      const block = new Uint8Array(r.bytes(NIBBLES_PER_SECTION));
      chunk.sections[sy] = new Section(blocks, sky, block);
    }
    for (let sy = 0; sy < SECTION_COUNT; sy++) {
      if ((mask & (1 << sy)) !== 0) continue;
      const mode = r.u8();
      if (mode === 2) {
        const section = new Section();
        section.skyLight.set(r.bytes(NIBBLES_PER_SECTION));
        section.blockLight.set(r.bytes(NIBBLES_PER_SECTION));
        chunk.sections[sy] = section;
      } else if (mode === 1) {
        // Empty and fully skylit; represented implicitly via the heightmap.
      }
    }

    const beCount = r.varint();
    for (let i = 0; i < beCount; i++) {
      const lx = r.u8();
      const y = r.i16();
      const lz = r.u8();
      const kind = r.str();
      chunk.setBlockEntity({ kind, x: cx * CHUNK_X + lx, y, z: cz * CHUNK_Z + lz, data: {} });
    }

    chunk.recomputeHeightmaps();
    chunk.status = 'ready';
    this.handlers.onChunk(chunk);
  }
}
